import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import baseUrl from '../../../../BaseURL';
import { ContextData } from '../../../../context/Context';
import ViewNIN from './ViewNIN';
import DialogReport from './DialogReport';
import MemberProfileView from './MemberProfileView';
import RejectedApplicationEditor from './RejectedApplicationEditor';
import VirtualIDCardDialog from './VirtualIDCardDialog';
import PaidMemberUpdateDialog from './PaidMemberUpdateDialog';
import './MemberProfile.css';

export default function MemberDataPage({ token, vr }) {
 const {accesstoken,Person,Refresh,setRefresh}=useContext(ContextData);
 const [profile,setProfile]=useState(null);
 const [loading,setLoading]=useState(false);
 const [error,setError]=useState('');
 const [message,setMessage]=useState('');
 const [editing,setEditing]=useState(false);
 const [busy,setBusy]=useState(false);
 useEffect(()=>{
  let active=true;const controller=new AbortController();
  setProfile(null);setError('');setEditing(false);
  if(!token)return ()=>controller.abort();
  setLoading(true);
  const legacy = !['0','100','90'].includes(String(Person?.acc_level));
  const profileUrl=legacy?`${baseUrl}/auth/member_data_page/${encodeURIComponent(token)}`:`${baseUrl}/auth/applications/${encodeURIComponent(token)}`;
  axios.get(profileUrl,{withCredentials:true,headers:{authorization:`Bearer ${accesstoken}`},signal:controller.signal})
   .then(({data})=>{if(active){const member=legacy?data[0]:data.member;if(!member)throw new Error('Application not found');setProfile(member);}})
   .catch(err=>{if(active)setError(err.response?.data?.message||'Unable to load this application. Please reopen the profile to try again.');})
   .finally(()=>{if(active)setLoading(false);});
  return ()=>{active=false;controller.abort();};
 },[token,Refresh,accesstoken,Person?.acc_level]);
 async function decide(decision,reason){
  if(busy)return;setBusy(true);setError('');
  try{
   const {data}=await axios.put(`${baseUrl}/auth/applications/${encodeURIComponent(token)}/decision`,{decision,reason},{withCredentials:true,headers:{authorization:`Bearer ${accesstoken}`}});
   if(data.status!==true)throw new Error('Application decision could not be saved');
   setMessage(data.message);setRefresh(previous=>!previous);
  }catch(err){if(decision==='reject')throw err;setError(err.response?.data?.message||'The application could not be verified. Please try again.');}
  finally{setBusy(false);}
 }
 if(!token)return <div className="fin-profile-loading">Select a member to view their application.</div>;
 if(loading)return <div className="fin-profile-loading" role="status"><CircularProgress size={24}/><p>Loading member details…</p></div>;
 if(!profile)return <Alert severity="error">{error||'Application not found.'}</Alert>;
 const status=Number(profile.acc_status);
 const owner=Person?.usertype==='user'&&String(Person?.acc_level)==='0'&&Person?.token===profile.registerer_token;
 const reviewer=['100','90'].includes(String(Person?.acc_level));
 return <>
  {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}
  {message&&<Alert severity="success" sx={{mb:2}}>{message}</Alert>}
  {status===4&&<Alert severity="error" sx={{mb:2}}><strong>Application rejected</strong><p style={{whiteSpace:'pre-wrap',margin:'6px 0'}}>{profile.rejection_reason||'Please correct the application details before resubmitting.'}</p>{owner&&'You can update this application and resubmit it for verification.'}</Alert>}
  {owner&&status!==4&&<Alert severity={status===3?'success':'info'} sx={{mb:2}}>{status===3?'Your application is approved. You can view your ID card or request a paid update for admin review.':'Your application is pending review. Updates are locked until a verifier makes a decision.'}</Alert>}
  <MemberProfileView data={profile} actions={vr==='vr'&&reviewer&&status===1?<>
   <Button onClick={()=>decide('verify')} disabled={busy} variant="contained">{busy?'Saving…':'Verified'}</Button>
   <DialogReport onReject={reason=>decide('reject',reason)} disabled={busy}/>
   {profile.ninFile&&profile.ninFile!=='N/A'&&<ViewNIN nin={profile.ninFile} ninId={profile.nin}/>}
  </>:null}/>
  {owner&&status===4&&!editing&&<div className="fin-application-buttons"><Button variant="contained" onClick={()=>{setMessage('');setEditing(true);}}>Update application</Button></div>}
  {owner&&status===4&&editing&&<RejectedApplicationEditor key={profile.token} data={profile} onCancel={()=>setEditing(false)} onSaved={text=>{setMessage(text);setEditing(false);setRefresh(previous=>!previous);}}/>}
  {status===3&&(owner||reviewer)&&<div className="fin-application-buttons"><VirtualIDCardDialog token={profile.token}/>{owner&&<PaidMemberUpdateDialog token={profile.token}/>}</div>}
 </>;
}
